import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, MoreVertical, GripVertical, FolderOpen } from 'lucide-react';
import { ChatFolder } from '@/services/folderService';
import { Chat } from '@/services/chatService';
import { DraggableChatItem } from './DraggableChatItem';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface FolderItemProps {
  folder: ChatFolder;
  chats: Chat[];
  plan: string;
  allFolders: ChatFolder[];
  onEditFolder: (folder: ChatFolder) => void;
  onDeleteFolder: (folder: ChatFolder) => void;
  onEditChat: (chat: Chat) => void;
  onDeleteChat: (chat: Chat) => void;
  onMoveToFolder: (chat: Chat, folderId: string | null) => void;
  onTogglePin?: (chat: Chat) => void;
  isOver?: boolean;
}

export function FolderItem({
  folder,
  chats,
  plan,
  allFolders,
  onEditFolder,
  onDeleteFolder,
  onEditChat,
  onDeleteChat,
  onMoveToFolder,
  onTogglePin,
  isOver,
}: FolderItemProps) {
  const [isOpen, setIsOpen] = useState(true);

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `folder-${folder.id}`,
    data: {
      type: 'folder',
      folder,
    },
  });

  const { setNodeRef: setDroppableRef, isOver: isDropOver } = useDroppable({
    id: `droppable-${folder.id}`,
    data: {
      type: 'folder',
      folderId: folder.id,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isHighlighted = isOver || isDropOver;

  return (
    <div
      ref={setSortableRef}
      style={style}
      className={`${isDragging ? 'z-50' : ''}`}
    >
      {/* Droppable wrapper for the entire folder */}
      <div
        ref={setDroppableRef}
        className={`rounded-lg transition-all duration-200 ${
          isHighlighted 
            ? 'bg-primary/10 ring-2 ring-primary/30 scale-[1.02]' 
            : ''
        }`}
      >
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="group flex items-center gap-1 px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
            <button
              className="p-0.5 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-opacity"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-3 w-3" />
            </button>

            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 flex-1 min-w-0 text-left py-0.5">
                {isHighlighted ? (
                  <FolderOpen className="h-4 w-4 text-primary flex-shrink-0" />
                ) : isOpen ? (
                  <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                )}
                <span className="text-base">{folder.icon}</span>
                <span 
                  className={`text-xs font-medium truncate flex-1 transition-colors ${
                    isHighlighted ? 'text-primary' : ''
                  }`}
                  style={{ color: isHighlighted ? undefined : folder.color }}
                >
                  {folder.name}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  ({chats.length})
                </span>
              </button>
            </CollapsibleTrigger>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => onEditFolder(folder)}>
                  ✏️ Editar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDeleteFolder(folder)}
                  className="text-destructive focus:text-destructive"
                >
                  🗑️ Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <CollapsibleContent>
            <div className="ml-4 pl-2 border-l-2 border-border/50 space-y-0.5 py-1">
              {chats.length === 0 ? (
                <p className={`text-[10px] px-2 py-2 italic text-center rounded ${
                  isHighlighted 
                    ? 'text-primary bg-primary/5' 
                    : 'text-muted-foreground'
                }`}>
                  {isHighlighted ? '📥 Suelta aquí' : 'Sin conversaciones'}
                </p>
              ) : (
                <SortableContext
                  items={chats.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {chats.map((chat) => (
                    <DraggableChatItem
                      key={chat.id}
                      chat={chat}
                      plan={plan}
                      folders={allFolders}
                      onEditChat={onEditChat}
                      onDeleteChat={onDeleteChat}
                      onMoveToFolder={onMoveToFolder}
                      onTogglePin={onTogglePin}
                    />
                  ))}
                </SortableContext>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
}
